import { useRef, useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AgGridReact } from 'ag-grid-react'
import { styled } from '@mui/material'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { PageArea } from '..'
import { ApplicationFunctionPermissionRow } from './types'
import { usePermissionPolicyOptions } from '../../hooks/usePermissionPolicyOptions'
import { usePermissionPolicyData } from './hooks/usePermissionPolicyData'
import { usePermissionPolicyGridOptions } from './gridOptions'
import { useBulkSheetState } from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { useColumnState } from '../../containers/BulkSheetView/hooks/columnState/useColumnState'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import ReloadButton from '../../containers/BulkSheetView/components/header/ReloadButton'
import FilterButton from '../../containers/BulkSheetView/components/header/FilterButton'
import SortButton from '../../containers/BulkSheetView/components/header/SortButton'
import ColumnSettingButton from '../../containers/BulkSheetView/components/header/ColumnSettingButton'
import { GridApi, GridReadyEvent } from 'ag-grid-community'
import Loading from '../../components/process-state-notifications/Loading'
import { UiStateKey } from '../../../lib/commons/uiStates'
import _ from 'lodash'
import { PermissionPolicyOption } from '../../../domain/value-object/PermissionPolicy'

type Props = {
  uuid: string
}

const PermissionPolicies = ({ uuid: functionUuid }: Props) => {
  const {
    initialized: permissionPolicyOptionsInitialized,
    allPermissionPolicyOptions,
    refetch: refetchPermissionPolicies,
  } = usePermissionPolicyOptions()

  if (!permissionPolicyOptionsInitialized) {
    return (
      <PageArea>
        <Loading isLoading={true} />
      </PageArea>
    )
  }

  return (
    <Page
      functionUuid={functionUuid}
      allPermissionPolicies={allPermissionPolicyOptions}
      refetchPermissionPolicies={refetchPermissionPolicies}
    />
  )
}

const Page = ({
  functionUuid,
  allPermissionPolicies,
  refetchPermissionPolicies,
}: {
  functionUuid: string
  allPermissionPolicies: PermissionPolicyOption[]
  refetchPermissionPolicies: () => Promise<void>
}) => {
  const dispatch = useDispatch()
  const gridRef = useRef<AgGridReact<ApplicationFunctionPermissionRow>>(null)
  const bulkSheetRef = useRef<HTMLDivElement>(null)
  const [isLoading, setIsLoading] = useState(false)

  const { rowData } = usePermissionPolicyData({
    permissionPolicyOptions: allPermissionPolicies,
  })

  const gridOptions = usePermissionPolicyGridOptions({
    permissionPolicyOptions: allPermissionPolicies,
  })

  const gridApiRef = useRef<GridApi>()
  const [isGridReady, setIsGridReady] = useState(false)

  const bulkSheetState = useBulkSheetState(
    functionUuid,
    UiStateKey.PermissionPolicyColumnAndFilterState
  )

  const columnState = useColumnState({
    gridApi: gridApiRef.current,
    autoGroupColumnDef: gridOptions.autoGroupColumnDef,
    bulkSheetState,
  })
  const columnSetting = useColumnSetting()

  const handleReload = useCallback(async () => {
    setIsLoading(true)
    try {
      await refetchPermissionPolicies()
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, refetchPermissionPolicies])

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      gridApiRef.current = event.api
      setIsGridReady(true)
      if (!_.isEmpty(bulkSheetState.columnState)) {
        event.api.applyColumnState({
          state: bulkSheetState.columnState,
          applyOrder: true,
        })
      }
    },
    [bulkSheetState.columnState]
  )

  const onFirstDataRendered = useCallback(() => {
    if (!gridApiRef.current) return

    if (!_.isEmpty(bulkSheetState.filterState)) {
      gridApiRef.current.setFilterModel(bulkSheetState.filterState ?? null)
    }
    bulkSheetState.finishRestoring()
  }, [bulkSheetState.filterState, bulkSheetState.finishRestoring])

  if (!bulkSheetState.initialized) {
    return <></>
  }
  return (
    <PageArea>
      <Loading isLoading={isLoading} elem={bulkSheetRef.current} />
      <Header>
        <ReloadButton onReload={handleReload} />
        <FilterButton
          filteredColumns={columnState.filteredColumns}
          onDeleteFilteredColumn={columnState.onDeleteFilteredColumn}
          resetFilters={columnState.resetFilters}
        />
        <SortButton
          onDeleteSortedColumn={columnState.onDeleteSortedColumn}
          onDeleteSortedAllColumns={columnState.onDeleteSortedAllColumns}
          onChangeSortColumnState={columnState.onChangeSortColumnState}
          sortColumnsState={columnState.sortColumnsState}
        />
        <ColumnSettingButton
          onClick={columnSetting.toggle}
          open={columnSetting.isOpen}
        />
      </Header>
      <BulkSheetView
        ref={bulkSheetRef}
        gridRef={gridRef}
        gridOptions={gridOptions}
        rowData={rowData}
        // Events and actions
        onGridReady={onGridReady}
        onFirstDataRendered={onFirstDataRendered}
        onColumnVisible={columnState.onColumnVisible}
        onColumnResized={columnState.rememberColumnState}
        onColumnMoved={columnState.rememberColumnState}
        onFilterChanged={columnState.onFilterChanged}
        onSortChanged={columnState.onSortChanged}
      />
      <ColumnSettingPopper
        anchorEl={columnSetting.anchorEl}
        open={columnSetting.isOpen}
        close={columnSetting.close}
        gridApi={gridApiRef.current || undefined}
        height={bulkSheetRef.current?.offsetHeight}
        openSavedUiStateDialog={undefined}
        initializeColumnState={columnState.resetColumnState}
        applicationFunctionUuid={functionUuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        columnState={bulkSheetState.columnState}
        offset={120}
      />
    </PageArea>
  )
}

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  margin: '10px 10px 5px 10px',
  overflowX: 'auto',
  overflowY: 'hidden',
  minHeight: '47px',
})

export default PermissionPolicies
